import { useQuery } from '@tanstack/react-query';
import { type AxiosError, type AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { QUERY_KEYS } from 'constants/common';
import { SURVEY_TYPES } from 'constants/surveys';
import { SurveyService } from 'services/surveys';
import { type SurveyType, type Question, type Survey, type SurveyResponse } from 'types/surveys';
import { getSurveyQuestions } from 'utils/surveys';
import { useGetSurveys } from './useGetSurveys';

export const useGetSurveyStatus = (
  surveyType: SurveyType,
  allowRepeat = false,
  surveyId?: string
): {
  survey?: Survey;
  surveyResponse?: SurveyResponse;
  questions?: Question[];
  isCompleted: boolean;
  isLoading: boolean;
} => {
  const { data: surveys, isLoading: isLoadingSurveys } = useGetSurveys(surveyType);

  const activeSurvey: Survey | undefined = ((): Survey | undefined => {
    if (surveyId && surveys?.length) {
      return surveys.find(survey => survey.id === surveyId);
    }
    if (!surveys?.length) return undefined;
    const incomplete = surveys.find(survey => !survey.isCompleted);
    if (incomplete) return incomplete;
    return allowRepeat ? surveys[surveys.length - 1] : undefined;
  })();

  const activeSurveyId = activeSurvey?.id ?? surveyId;

  const isDailyTracking = surveyType === SURVEY_TYPES.DAILY;
  const today = isDailyTracking ? format(new Date(), 'yyyy-MM-dd') : undefined;

  const { data: surveyResponse, isLoading: isLoadingResponses } = useQuery({
    queryKey: today
      ? [QUERY_KEYS.FETCH_SURVEY_RESPONSES, activeSurveyId, today]
      : [QUERY_KEYS.FETCH_SURVEY_RESPONSES, activeSurveyId],
    queryFn: () => SurveyService.getSurveyResponses(activeSurveyId ?? ''),
    select: res => res.data,
    enabled: Boolean(activeSurveyId),
  });

  const isReallyCompleted = surveyResponse?.completed ?? activeSurvey?.isCompleted ?? false;

  const answersCount = surveyResponse?.answers?.length ?? activeSurvey?.answers?.length ?? 0;

  const progressIndex =
    surveyResponse?.currentQuestionIndex ?? activeSurvey?.currentQuestionIndex ?? 0;

  const hasPreviousResponses = answersCount > 0 || progressIndex > 0;

  const surveyQuestions = getSurveyQuestions(activeSurvey);
  const isLastQuestion = surveyQuestions && surveyQuestions.length - 1 === progressIndex;

  const { data: fetchedQuestions, isLoading: isLoadingQuestions } = useQuery<
    AxiosResponse<Question[]>,
    AxiosError,
    Question[]
  >({
    queryKey: today
      ? [QUERY_KEYS.FETCH_SURVEY_QUESTIONS, activeSurveyId, today]
      : [QUERY_KEYS.FETCH_SURVEY_QUESTIONS, activeSurveyId],
    queryFn: () => SurveyService.getNextSurveyQuestions(activeSurveyId ?? ''),
    select: response => response.data,
    enabled: Boolean(
      activeSurveyId && !isReallyCompleted && hasPreviousResponses && !isLastQuestion
    ),
    retryOnMount: false,
    throwOnError: false,
  });

  const questions =
    isReallyCompleted || (hasPreviousResponses && !fetchedQuestions)
      ? undefined
      : (fetchedQuestions ?? getSurveyQuestions(activeSurvey));

  const isCompleted = allowRepeat
    ? isReallyCompleted
    : Boolean(surveys?.every(survey => survey.isCompleted));

  const isLoading = isLoadingSurveys || isLoadingResponses || isLoadingQuestions;

  return {
    survey: activeSurvey,
    surveyResponse,
    questions,
    isCompleted,
    isLoading,
  };
};
